/**
 * 데모 모드 API 인터셉터
 *
 * 데모 모드일 때 window.fetch 를 가로채서, ERP 서버로 나가야 할 요청을
 * demoData.js 의 샘플 데이터로 대신 응답한다. 실제 서버로는 아무 요청도 나가지 않는다.
 *
 * 왜 fetch 를 가로채는가:
 *   apiService.js 만 해도 9,000줄이 넘고 화면마다 개별 fetch 를 직접 부른다.
 *   함수를 하나씩 데모용으로 갈아끼우는 대신, 모든 호출이 반드시 지나가는
 *   길목(fetch) 한 곳만 막으면 화면 코드를 건드리지 않아도 된다.
 *
 * ── 샘플 응답을 추가하는 방법 ────────────────────────────────
 *   1) 개발자도구 콘솔에서 `[demo] 미등록 프로시저: ...` 경고에 찍힌 이름을 확인한다.
 *   2) 아래 HANDLERS 에 그 이름을 키로 넣고, 돌려줄 데이터를 반환하는 함수를 쓴다.
 *   3) 등록하지 않은 조회는 빈 목록, 저장은 성공으로 응답하므로 화면이 깨지지는 않는다.
 */

import { isDemoMode, DEMO_USER_NO } from './demoMode';
import {
  DEMO_AGENCIES,
  DEMO_BANK_ACCOUNTS,
  DEMO_CODES,
  DEMO_COMPANY,
  DEMO_CONSULTS,
  DEMO_CONTENT_TYPES,
  DEMO_DETAIL_CONTENT_ITEMS,
  DEMO_ITINERARY_DETAIL,
  DEMO_ITINERARY_TYPES,
  DEMO_MEETING_PLACE,
  DEMO_PLAN_BASE,
  DEMO_PRICE_ROWS,
  DEMO_PRODUCTS,
  DEMO_PRODUCT_DETAIL_EXTRA,
  DEMO_RATE_PLANS,
  DEMO_REP_IMAGES,
  DEMO_RESOURCES,
  DEMO_SALE_STATUS,
  DEMO_SELECT_OPTIONS,
  DEMO_SHARED_INVENTORY,
  DEMO_STAFF,
  buildBankReceipts,
  buildCancelInfo,
  buildConsultInfo,
  buildConsultListRow,
  buildConsultNotes,
  buildDepositData,
  buildGroupSettlementRows,
  buildOrderInfo,
  buildOrderStatusRows,
  buildOutstandingRows,
  buildPriceCalendarRows,
  buildPriceInfo,
  buildRequestItems,
  buildReservationLogs,
  buildSaleInfoRow,
  buildSaleStatusRow,
  buildTravelerDetailRows,
  buildTravelerListRows,
  consultStatusNameOf,
  dateTimeFromNow,
  dayFromNow,
  findDemoOrder,
  findDemoOrderBySaleNo,
} from './demoData';

/** 데모가 가로챌 호스트 */
const API_HOSTS = ['trpliveapi1.tripbox.co.kr'];

/** 응답이 너무 빨리 돌아오면 로딩 UI가 깜빡여서 어색하다. 살짝 지연시킨다. */
const FAKE_LATENCY_MS = 160;

// ────────────────────────────────────────────────────────────────
// 데모 세션 동안만 살아 있는 메모리 저장소
//   새로고침하면 원래 샘플로 되돌아간다. (데모의 "초기화" 역할)
// ────────────────────────────────────────────────────────────────

const store = {
  products: null,
  nextProductNo: 990200,
  // 가격/재고 화면에서 사용자가 저장한 값. 달력을 다시 그릴 때 기본값 위에 덮어씌운다.
  priceOverrides: {},
  stockOverrides: {},
  closedOverrides: {},
  // 예약 화면에서 사용자가 바꾼 값. 판매번호를 키로 쓴다.
  saleMemos: {},
  saleStatuses: {},

  // ── 예약 상세 하위 기능 ──
  // 여행객 명단. 룸번호 · 명단 추가/삭제 결과가 여기 쌓인다. 판매번호를 키로 쓴다.
  travelers: {},
  nextTravelerNo: 995001,
  listLocked: {},
  // 취소 처리 / 대리점 커미션
  cancelFees: {},
  commissions: {},
  // 예약기록(로그)
  logs: {},
  nextLogNo: 970001,
  // 입금내역. deposits 는 우리가 붙인 줄, removedDeposits 는 지운 원래 줄의 결제번호다.
  deposits: {},
  removedDeposits: {},
  nextPaymentNo: 960001,
  // 회사 통장 입금 목록. 예약별이 아니라 회사 전체가 같이 본다.
  receipts: null,
  nextReceiptNo: 78000,
  // 주문정보 화면에서 고친 주문자 / 대리점. 주문번호를 키로 쓴다.
  orderers: {},
  agencies: {},

  // ── 상담(견적) 관리 ──
  // 상담은 저장·삭제가 목록에 바로 보여야 하는 화면이라, 줄 자체를 여기서 들고 고친다.
  consults: null,
  nextConsultNo: 980200,
  // 상담기록(상담내역). 상담번호를 키로 쓴다.
  consultNotes: {},
  nextConsultNoteNo: 985001,

  // ── 예약시 받을 정보 ──
  // 서버와 같은 두 층 구조다. bookingFields 는 업체 단위 "필드 정의"(상품번호 없음),
  // productBookingFields 는 상품별로 켠 필드번호 목록이다.
  bookingFields: null,
  nextBookingFieldNo: 940001,
  productBookingFields: {},
  // 상품별 상세정보(신청금 설정 등). 상품번호를 키로 쓴다.
  productDetails: {},
};

function getProducts() {
  if (!store.products) store.products = DEMO_PRODUCTS.map((p) => ({ ...p }));
  return store.products;
}

/** 데모 데이터를 처음 상태로 되돌린다. */
export function resetDemoStore() {
  store.products = null;
  store.nextProductNo = 990200;
  store.priceOverrides = {};
  store.stockOverrides = {};
  store.closedOverrides = {};
  store.saleMemos = {};
  store.saleStatuses = {};

  store.travelers = {};
  store.nextTravelerNo = 995001;
  store.listLocked = {};
  store.cancelFees = {};
  store.commissions = {};
  store.logs = {};
  store.nextLogNo = 970001;
  store.deposits = {};
  store.removedDeposits = {};
  store.nextPaymentNo = 960001;
  store.receipts = null;
  store.nextReceiptNo = 78000;
  store.orderers = {};
  store.agencies = {};

  store.consults = null;
  store.nextConsultNo = 980200;
  store.consultNotes = {};
  store.bookingFields = null;
  store.nextBookingFieldNo = 940001;
  store.productBookingFields = {};
  store.productDetails = {};
  store.nextConsultNoteNo = 985001;
}

/**
 * 예약 화면에서 저장한 값(판매비고 · 예약상태)을 응답 줄에 덮어씌운다.
 * 예약 목록은 원본 DEMO_ORDERS 에서 매번 새로 만들기 때문에, 저장한 값은 여기서 다시 얹어야
 * 화면이 "방금 바꾼 대로" 보인다.
 */
/** 취소로 치는 예약상태 코드. 화면은 이 상태일 때만 [예약복원] 과 취소료 칸을 보여준다. */
const CANCEL_STATUS_CODES = ['049021', '049022', '049029'];

function applySaleOverrides(rows) {
  return rows.map((row) => {
    const key = String(row.판매번호 ?? '');
    const memo = store.saleMemos[key];
    const status = store.saleStatuses[key];
    if (memo === undefined && status === undefined) return row;

    const next = { ...row };
    if (memo !== undefined) next.판매비고 = memo;
    if (status !== undefined) {
      const name = (DEMO_SALE_STATUS.find((s) => s.코드 === String(status)) || {}).코드명 || '';
      next.예약상태코드 = status;
      if ('예약상태' in next) next.예약상태 = name;
      if ('예약상태2' in next) next.예약상태2 = name;
      if ('판매상태' in next) next.판매상태 = name;
      // 상태만 바꾸고 취소여부를 그대로 두면, 취소해 놓고도 [예약취소] 버튼이 계속 뜬다.
      if ('취소여부' in next) next.취소여부 = CANCEL_STATUS_CODES.includes(String(status)) ? '1' : '0';
    }
    return next;
  });
}

// ────────────────────────────────────────────────────────────────
// 유틸
// ────────────────────────────────────────────────────────────────

function toUrlString(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input && typeof input.url === 'string') return input.url; // Request
  return '';
}

function isApiUrl(urlString) {
  try {
    const u = new URL(urlString, window.location.origin);
    return API_HOSTS.includes(u.hostname);
  } catch (_) {
    return false;
  }
}

/** 코드마다 인코딩 상태가 달라서(원본 한글 / %EC%.. 혼용) 안전하게 반복 디코딩한다. */
function decodeProcedureName(raw) {
  let value = String(raw || '');
  for (let i = 0; i < 3; i += 1) {
    if (!/%[0-9A-Fa-f]{2}/.test(value)) break;
    try {
      const next = decodeURIComponent(value);
      if (next === value) break;
      value = next;
    } catch (_) {
      break;
    }
  }
  return value.replace(/^DBO\./i, '').trim();
}

/** 요청 본문의 Table1 을 { '@파라미터명': 값 } 형태로 바꾼다. */
function parseParams(body) {
  const out = {};
  try {
    const json = typeof body === 'string' ? JSON.parse(body) : body;
    const rows = json?.Table1 || json?.table1 || [];
    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        const key = row?.컬럼명 ?? row?.['컬럼명'];
        if (key) out[String(key)] = row?.값;
      });
    }
  } catch (_) {}
  return out;
}

function jsonResponse(payload, status = 200) {
  return new Response(JSON.stringify(payload), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** 조회 응답 기본 포장. 소문자/대문자 키를 모두 채워 호출부 차이를 흡수한다. */
function dataset(table = [], extra = {}) {
  const rows = Array.isArray(table) ? table : [table];
  return {
    table: rows,
    Table: rows,
    table1: rows,
    Table1: rows,
    ...extra,
  };
}

/** 저장/삭제 성공 응답 */
function successResult(message = '데모 모드에서 저장된 것처럼 처리했습니다.') {
  return dataset([
    { 결과: 'success', 성공여부: true, 메시지: message, message, RESULT: 'success', 오류: '' },
  ]);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 업체 단위 필드 정의 목록. 처음에는 비어 있다 — 실제로도 새 업체는 아무 필드가 없다. */
function getBookingFields() {
  if (!store.bookingFields) store.bookingFields = [];
  return store.bookingFields;
}

/** 그 상품에서 켜 둔 필드번호 목록 */
function getProductBookingFieldNos(productNo) {
  return store.productBookingFields[String(productNo ?? '')] || [];
}

/** 저장은 코드로 오고 조회는 이름으로 나간다. 017001 Text / 017002 List / 017003 Checkbox */
function dataTypeName(code) {
  switch (String(code ?? '')) {
    case '017002':
      return 'List';
    case '017003':
      return 'Checkbox';
    default:
      return 'Text';
  }
}

function findProduct(no) {
  const key = String(no ?? '');
  return getProducts().find((p) => String(p.상품번호) === key || String(p.상품코드) === key) || null;
}

/**
 * 처음부터 샘플로 깔려 있던 상품인지.
 * 데모 중에 새로 만든 상품(990200번대)은 가격도 재고구분도 없는 빈 상태로 둔다.
 */
function isSeededProduct(no) {
  const n = Number(no);
  return Number.isFinite(n) && n < 990200;
}

// ── 가격/재고 저장 payload 해석 ──────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * 적용기간 한 칸을 실제 날짜 목록으로 편다.
 * 요일2 는 월~일 순서의 7자리 마스크('YNNNNYY' 처럼)다. JS 의 getDay() 는 일요일이 0이라
 * (getDay() + 6) % 7 로 자리를 맞춘다.
 */
function expandPeriod(period) {
  const start = String(period?.시작일 || '').slice(0, 10);
  const end = String(period?.종료일 || start).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return [];

  const mask = typeof period?.요일2 === 'string' && period.요일2.length === 7 ? period.요일2 : 'YYYYYYY';
  const from = new Date(Number(start.slice(0, 4)), Number(start.slice(5, 7)) - 1, Number(start.slice(8, 10)));
  const to = /^\d{4}-\d{2}-\d{2}$/.test(end)
    ? new Date(Number(end.slice(0, 4)), Number(end.slice(5, 7)) - 1, Number(end.slice(8, 10)))
    : new Date(from);

  const out = [];
  const cursor = new Date(from);
  // 실수로 거대한 범위가 들어와도 데모가 멈추지 않도록 상한을 둔다.
  let guard = 0;
  while (cursor <= to && guard < 800) {
    const maskIndex = (cursor.getDay() + 6) % 7;
    if (String(mask[maskIndex] || 'Y').toUpperCase() === 'Y') out.push(ymd(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return out;
}

/** 판매가 목록에서 항목 하나를 [성인, 소아, 유아] 숫자 배열로 꺼낸다. */
function readAmountRow(list, itemName) {
  const rows = Array.isArray(list) ? list : [];
  const row = rows.find((r) => String(r?.항목 || '') === itemName);
  if (!row) return null;
  const toNum = (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  return [toNum(row.성인), toNum(row.어린이), toNum(row.영유아)];
}

// ────────────────────────────────────────────────────────────────
// 예약 상세 하위 기능 — 데모 중에 값이 실제로 바뀌는 부분
//
// 취소 · 통장 입금 매칭 · 룸 배정 · 기록은 눌러도 아무 일이 없으면 데모가 아니라 스크린샷이 된다.
// 그래서 아래 함수들이 메모리 데이터를 진짜로 고친다. 새로고침하면 처음 상태로 돌아간다.
// ────────────────────────────────────────────────────────────────

/** 숫자만 뽑아낸다. 화면이 '1,200,000' 같은 문자열로 보내는 경우가 있다. */
function toNumber(value) {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

/** 'YYYY-MM-DD HH:mm' — 예약기록의 작성일 형식 */
function nowStamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── 예약기록(로그) ───────────────────────────────────────────

function getLogs(saleNo) {
  const key = String(saleNo ?? '');
  if (!store.logs[key]) {
    const order = findDemoOrderBySaleNo(key);
    store.logs[key] = order ? buildReservationLogs(order) : [];
  }
  return store.logs[key];
}

/**
 * 기록을 한 줄 남긴다.
 * 취소나 입금 같은 처리를 할 때마다 같이 불러 준다. 데모에서 [예약기록] 을 열었을 때
 * 방금 한 일이 그대로 찍혀 있어야, 이 화면이 무엇을 하는 화면인지 설명이 된다.
 */
function addLog(saleNo, 태그, 내용) {
  store.nextLogNo += 1;
  const row = {
    참조번호: store.nextLogNo,
    판매번호: Number(saleNo) || saleNo,
    태그,
    내용,
    작성일: nowStamp(),
    유저번호: DEMO_USER_NO,
    작성자: DEMO_STAFF.한글이름,
  };
  getLogs(saleNo).unshift(row);
  return row;
}

/** 참조번호로 기록 한 줄을 찾는다. 어느 판매의 기록인지는 화면이 알려주지 않는다. */
function findLog(referenceNo) {
  const key = String(referenceNo ?? '');
  const saleKey = Object.keys(store.logs).find((s) =>
    store.logs[s].some((row) => String(row.참조번호) === key)
  );
  if (!saleKey) return null;
  const rows = store.logs[saleKey];
  return { rows, index: rows.findIndex((row) => String(row.참조번호) === key) };
}

// ── 여행객 명단 (룸 배정 · 명단 추가/삭제) ───────────────────

/**
 * 여행객 저장 payload 의 필드번호 ↔ 실제 컬럼 이름.
 * 화면은 여행객 정보를 { 여행객번호, 필드번호, 값 } 목록으로 보낸다. -1 이 룸번호다.
 */
const TRAVELER_FIELDS = {
  '-1': '룸번호',
  8: '한글이름',
  9: '영문성',
  10: '영문이름',
  11: '생년월일',
  12: '휴대폰',
  109: '성별',
  253: '여권번호',
  254: '여권만료일',
  256: '이메일',
  314: '여행객비고',
};

/** 그 판매의 여행객 명단. 처음 열 때 샘플로 채우고, 그 뒤로는 저장한 내용이 남는다. */
function getRoster(saleNo) {
  const key = String(saleNo ?? '');
  if (!store.travelers[key]) {
    const order = findDemoOrderBySaleNo(key);
    store.travelers[key] = order ? buildTravelerDetailRows(order) : [];
  }
  return store.travelers[key];
}

/**
 * 명단에 새로 추가된 사람.
 * 화면은 아직 번호가 없는 사람에게 임시 UUID 를 붙여 보낸다. 실제 서버가 번호를 매겨 돌려주는
 * 자리라서, 데모에서도 여기서 진짜 번호를 붙여 줘야 저장 후 조회가 이어진다.
 */
function newTravelerRow(saleNo) {
  const order = findDemoOrderBySaleNo(saleNo);
  store.nextTravelerNo += 1;
  const no = store.nextTravelerNo;
  return {
    여행객번호: no,
    여행객키: String(no),
    판매번호: Number(saleNo) || saleNo,
    주문번호: order ? order.주문번호 : '',
    판매출발일: order ? dayFromNow(order.출발오프셋) : '',
    여행객종류: '여행객',
    연령구분: '성인',
    한글이름: '',
    성별: '',
    영문성: '',
    영문이름: '',
    생년월일: '',
    여권번호: '',
    여권만료일: '',
    여행객비고: '',
    휴대폰: '',
    이메일: '',
    룸번호: '',
    상품금액원화: 0,
    택스및유류원화: 0,
    공급단가: 0,
  };
}

function applyTravelerField(row, 필드번호, 값) {
  const name = TRAVELER_FIELDS[String(필드번호)];
  if (!name) return; // 데모에서 안 쓰는 필드(주민등록번호 등)는 흘려보낸다
  row[name] = 값 == null ? '' : 값;
}

function parseJsonParam(raw, fallback) {
  try {
    const parsed = JSON.parse(String(raw ?? ''));
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
}

// ── 입금내역 · 통장 입금(수납) ───────────────────────────────

function getReceipts() {
  if (!store.receipts) store.receipts = buildBankReceipts();
  return store.receipts;
}

/**
 * 그 판매의 입금내역과 위쪽 요약(입금액 / 미수금).
 *
 * 원래 깔려 있던 줄에서 지운 것을 빼고, 통장에서 붙인 줄을 더한 뒤 합계를 매번 다시 센다.
 * 합계를 따로 저장해 두지 않는 이유는, 한쪽만 고쳐져서 목록과 숫자가 어긋나는 걸 막기 위해서다.
 */
function depositDataFor(saleNo) {
  const key = String(saleNo ?? '');
  const order = findDemoOrderBySaleNo(key);
  if (!order) return { list: [], summary: { 입금액: 0, 미수금: 0, 총판매가: 0 } };

  const base = buildDepositData(order);
  const removed = store.removedDeposits[key] || [];
  const list = base.list
    .filter((row) => !removed.includes(String(row.결제번호)))
    .concat(store.deposits[key] || []);

  const 입금액 = list.reduce((sum, row) => sum + toNumber(row.입금액), 0);
  const 총판매가 = base.summary.총판매가;
  return {
    list,
    summary: { 입금액, 미수금: Math.max(0, 총판매가 - 입금액), 총판매가 },
  };
}

function addDepositRow(saleNo, row) {
  const key = String(saleNo ?? '');
  if (!store.deposits[key]) store.deposits[key] = [];
  store.nextPaymentNo += 1;
  const next = { 결제번호: store.nextPaymentNo, ...row };
  store.deposits[key].push(next);
  return next;
}

/** 원래 깔려 있던 입금 줄을 지운 것으로 표시한다. */
function markDepositRemoved(saleNo, paymentNo) {
  const key = String(saleNo ?? '');
  if (!store.removedDeposits[key]) store.removedDeposits[key] = [];
  store.removedDeposits[key].push(String(paymentNo));
}

// ── 상담(견적) 관리 ─────────────────────────────────────────
//
// 상담 화면은 "적어 두고 → 고치고 → 견적서를 만들고 → 예약으로 넘긴다" 가 전부다.
// 눌러도 목록이 그대로면 그 흐름이 화면에서 보이지 않으므로, 저장·삭제가 아래 메모리
// 데이터를 실제로 고친다. 새로고침하면 처음 샘플로 돌아간다.

function getConsults() {
  if (!store.consults) store.consults = DEMO_CONSULTS.map((c) => ({ ...c }));
  return store.consults;
}

function findConsult(consultNo) {
  const key = String(consultNo ?? '');
  return getConsults().find((c) => String(c.상담번호) === key) || null;
}

/** 그 상담의 상담기록. 처음 열 때 샘플로 채우고, 그 뒤로는 고친 내용이 남는다. */
function consultNotesOf(consultNo) {
  const key = String(consultNo ?? '');
  if (!store.consultNotes[key]) {
    const consult = findConsult(key);
    store.consultNotes[key] = consult ? buildConsultNotes(consult) : [];
  }
  return store.consultNotes[key];
}

/** 'YYYY-MM-DD' 문자열끼리 비교한다. 조회기간 필터는 이 정도로 충분하다. */
function withinDateRange(value, start, end) {
  const v = String(value || '').slice(0, 10);
  if (!v) return false;
  if (start && v < String(start).slice(0, 10)) return false;
  if (end && v > String(end).slice(0, 10)) return false;
  return true;
}

/** 저장 요청의 @상담정보 는 JSON 문자열이다. 실패하면 null (그냥 성공으로 응답한다) */
function parseConsultInfo(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return parsed[0] || null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

/** 팀컬러 칸에서 상품번호를 뽑는다. 배열 / 문자열 / 숫자가 섞여 들어온다. */
function readTeamColor(value) {
  const first = Array.isArray(value) ? value[0] : value;
  const digits = String(first ?? '').replace(/[^0-9]/g, '');
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readCostRows(list) {
  if (!Array.isArray(list)) return null;
  return list.map((row) => ({
    원가항목: String(row?.원가항목 || ''),
    성인: toNumber(row?.성인),
    어린이: toNumber(row?.어린이),
    영유아: toNumber(row?.영유아),
  }));
}

/**
 * 화면이 보낸 상담정보를 우리 샘플 줄에 덮어씌운다.
 * 보내지 않은 칸은 건드리지 않는다. 저장 payload 는 화면마다 채우는 범위가 달라서,
 * 없는 값을 빈 값으로 밀어 버리면 저장할 때마다 데이터가 깎여 나간다.
 */
function applyConsultInfo(target, info) {
  const put = (key, value) => {
    if (value !== undefined && value !== null) target[key] = value;
  };

  put('견적명', info.견적명);
  put('견적내용', info.견적내용);
  put('여행지도시', info.여행지도시);
  put('요청자명', info.요청자명);
  put('요청자휴대폰', info.요청자휴대폰);
  put('요청자이메일', info.요청자이메일);
  put('시작일', info.시작일);
  put('종료일', info.종료일);
  put('판매업체', info.판매업체);
  put('판매업체번호', info.판매업체번호);

  if (info.성인수 !== undefined) target.성인수 = toNumber(info.성인수);
  if (info.소아수 !== undefined) target.소아수 = toNumber(info.소아수);
  if (info.유아수 !== undefined) target.유아수 = toNumber(info.유아수);
  if (info.성인판매가 !== undefined) target.성인판매가 = toNumber(info.성인판매가);
  if (info.소아판매가 !== undefined) target.소아판매가 = toNumber(info.소아판매가);
  if (info.유아판매가 !== undefined) target.유아판매가 = toNumber(info.유아판매가);

  // 저장 payload 의 상담상태 자리에는 이름이 아니라 코드가 들어온다.
  const status = String(info.상담상태코드 ?? info.상담상태 ?? '');
  if (consultStatusNameOf(status)) target.상담상태코드 = status;

  const 세부원가 = readCostRows(info.세부원가);
  if (세부원가 && 세부원가.length) target.세부원가 = 세부원가;

  // 팀컬러를 비워서 보내면 "상품 연결을 끊는" 뜻이다. 그 경우만 null 로 둔다.
  if (info.팀컬러 !== undefined) target.상품번호 = readTeamColor(info.팀컬러);
}

/** 신규 견적 등록(ITN_T_상담저장)으로 만들어지는 상담 한 줄 */
function newConsult(info) {
  store.nextConsultNo += 1;
  const consult = {
    상담번호: store.nextConsultNo,
    상품번호: null,
    상담상태코드: '420001',
    견적명: '새 견적',
    여행지도시: '',
    요청자명: '',
    요청자휴대폰: '',
    요청자이메일: '',
    담당자: DEMO_STAFF.한글이름,
    담당자번호: DEMO_USER_NO,
    판매업체: '',
    판매업체번호: '',
    // 목록은 등록일시 최신순이다. 방금 만든 건이 맨 위에 있어야 흐름이 이어진다.
    등록일시: nowStamp(),
    시작일: '',
    종료일: '',
    성인수: 0,
    소아수: 0,
    유아수: 0,
    성인판매가: 0,
    소아판매가: 0,
    유아판매가: 0,
    세부원가: [],
    기록: [],
  };
  if (info) applyConsultInfo(consult, info);
  getConsults().unshift(consult);
  return consult;
}

// ────────────────────────────────────────────────────────────────
// 프로시저별 응답
//   키는 'DBO.' 를 뗀 한글 프로시저 이름
// ────────────────────────────────────────────────────────────────

const HANDLERS = {
  // ── 조회 ────────────────────────────────────────────────
  TRP_S_투어상품리스트: () => dataset(getProducts()),

  TRP_S_투어_기본정보: ({ params }) => {
    const product = findProduct(params['@상품번호']);
    if (!product) return dataset([]);
    return dataset([
      {
        ...DEMO_PRODUCT_DETAIL_EXTRA,
        ...product,
        도시번호: (DEMO_CODES.도시.find((c) => c.코드명 === product.도시) || {}).코드 || '',
        상품타입코드:
          (DEMO_CODES.투어상품타입.find((c) => c.코드명 === product.상품타입) || {}).코드 || '',
        키워드: String(product.키워드 || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      },
    ]);
  },

  // 도시 / 담당자 / 상품타입 등이 전부 이 프로시저 하나를 @종류 로 나눠 쓴다.
  RCT_S_수동코드: ({ params }) => {
    const kind = String(params['@종류'] || '');
    if (kind === '판매상태2') return dataset(DEMO_SALE_STATUS);
    return dataset(DEMO_CODES[kind] || []);
  },

  CW_S_유저정보: () => dataset([DEMO_COMPANY]),
  CW_S_사원정보: () => dataset([DEMO_STAFF]),
  CW_S_사원: () => dataset([DEMO_STAFF]),

  // table8 은 회사 입금 계좌 목록이다. 예약 상세에서 수납을 손으로 등록할 때
  // "어느 계좌로 들어온 돈인지" 고르는 드롭다운이 이 자리를 읽는다.
  RCT_S_회사기본정보: () =>
    dataset(
      [
        {
          업체번호: DEMO_COMPANY.업체번호,
          업체명: DEMO_COMPANY.업체명,
          대표자명: '김대표',
          사업자번호: '000-00-00000',
          대표전화: '02-6080-8800',
          대표이메일: 'demo@tripbox.co.kr',
          주소: '서울특별시 중구 데모로 1',
          회사로고: '',
        },
      ],
      { table8: DEMO_BANK_ACCOUNTS, Table8: DEMO_BANK_ACCOUNTS }
    ),

  // 상세페이지의 구역 목록.
  //  - 스마트 등록 2단계 검토 탭은 구역 이름만 쓴다.
  //  - 상세페이지 설정 화면은 구역별로 실제 글(컨텐츠)까지 그린다.
  // 새로 만든 상품은 구역은 있되 글은 비어 있는 게 맞다.
  RCT_S_콘텐츠메인리스트: ({ params }) => {
    const seeded = isSeededProduct(params['@상품번호']);
    return dataset(
      DEMO_CONTENT_TYPES.map((type) => ({
        ...type,
        컨텐츠: JSON.stringify(seeded ? DEMO_DETAIL_CONTENT_ITEMS[type.코드명] || [] : []),
      }))
    );
  },

  // ── 상세페이지 설정 화면 ────────────────────────────────
  RCT_S_상품_대표이미지: ({ params }) =>
    dataset(isSeededProduct(params['@상품번호']) ? DEMO_REP_IMAGES : []),

  RCT_S_상품갤러리: ({ params }) =>
    dataset(isSeededProduct(params['@상품번호']) ? DEMO_REP_IMAGES : []),

  TRP_S_투어미팅장소설정값: ({ params }) =>
    dataset(isSeededProduct(params['@상품번호']) ? [{ 투어미팅비고: DEMO_MEETING_PLACE }] : [{ 투어미팅비고: '' }]),

  TRP_S_선택옵션: ({ params }) =>
    dataset(isSeededProduct(params['@상품번호']) ? DEMO_SELECT_OPTIONS : []),

  // 일정타입. 새 상품에도 기본 일정타입 한 개는 있어야 일정표 편집이 열린다.
  RCT_S_상품일정타입: () =>
    dataset(DEMO_ITINERARY_TYPES, { table1: [{ 미팅값: DEMO_MEETING_PLACE }] }),

  // 일정표 본문. table1=일차별 일정, table3=식사, table5=호텔
  RCT_S_상품일정_02: ({ params }) => {
    if (!isSeededProduct(params['@상품번호'])) {
      return dataset([], { table1: [], table2: [], table3: [], table4: [], table5: [] });
    }
    return dataset([], {
      table1: DEMO_ITINERARY_DETAIL.table1,
      table2: [],
      table3: DEMO_ITINERARY_DETAIL.table3,
      table4: [],
      table5: DEMO_ITINERARY_DETAIL.table5,
    });
  },

  TRP_S_투어_판매가: () => dataset(DEMO_PRICE_ROWS),

  // ── 가격/재고 화면 ──────────────────────────────────────
  // 데모 중에 새로 만든 상품은 아직 재고구분도 가격도 없는 게 맞다.
  // (실제로도 상품을 만들자마자 이 화면은 비어 있다)
  RCT_S_투어상품자원리스트: ({ params }) =>
    dataset(isSeededProduct(params['@상품번호']) ? DEMO_RESOURCES : []),

  RCT_S_투어요금제리스트_02: ({ params }) => {
    if (!isSeededProduct(params['@상품번호'])) return dataset([]);
    const resourceNo = Number(params['@자원번호']);
    return dataset(DEMO_RATE_PLANS[resourceNo] || []);
  },

  // 달력 본체. 요청받은 기간만큼 그때그때 만들어 준다.
  RCT_S_가격예약_02: ({ params }) => {
    if (!isSeededProduct(params['@상품번호'])) return dataset([]);
    return dataset(
      buildPriceCalendarRows(params['@시작일'], params['@종료일'], {
        price: store.priceOverrides,
        stock: store.stockOverrides,
        closed: store.closedOverrides,
      })
    );
  },

  // 공유재고 목록. 여러 상품이 같은 좌석 묶음을 나눠 쓸 때 고르는 대상이다.
  TRP_S_공유재고: () => dataset(DEMO_SHARED_INVENTORY),

  // ── 예약시 받을 정보 ──────────────────────────────────
  // 필드 "정의"는 업체 단위라 상품번호가 없다. 상품별로는 켰는지 여부만 따로 둔다.
  // 이 구분이 실제 서버(DBO.필드 / DBO.예약정보필드)와 같아야, 단품 생성 때 도는
  // 자동 추가가 데모에서도 같은 방식으로 동작한다.

  TRP_S_투어_상세정보: ({ params }) => {
    const productNo = String(params['@상품번호'] ?? '');
    const on = getProductBookingFieldNos(productNo);
    const fields = getBookingFields().map((f) => ({
      관리주체: '업체',
      필드번호: f.필드번호,
      필드라벨: f.필드명,
      필드명: f.필드명,
      데이터타입: f.데이터타입,
      표시여부: on.includes(f.필드번호),
      필수여부: on.includes(f.필드번호),
      옵션목록: [],
      노출순서: f.순서,
    }));
    // table 은 상세정보 한 줄, table1 이 필드 목록이다.
    const saved = store.productDetails[productNo] || {};
    const detail = {
      상품번호: Number(productNo) || 0,
      현지지불코드: '0',
      신청금: '100',
      신청금구분: '004999', // 새 상품은 신청금 없음에서 시작한다
      // 서버(CW_T_투어_상품추가)의 상품 생성 기본값과 같게 둔다. 단품은 출발일이
      // 2036-12-31 이라, 이 기본값 그대로면 "2036년까지 내면 됨"이 된다.
      신청금기준일: '203001', // 출발일
      신청금시한: 0,
      완납기준일: '205001',
      완납시한: 0,
      연령성인끝: 100,
      ...saved,
    };
    return dataset([detail], { table1: fields, Table1: fields });
  },

  // 필드 정의 추가/수정 (@구분 I/U)
  TRP_T_필드: ({ params }) => {
    const name = String(params['@필드명'] ?? '').trim();
    if (String(params['@구분'] ?? '') === 'I' && name) {
      const list = getBookingFields();
      const 필드번호 = store.nextBookingFieldNo;
      store.nextBookingFieldNo += 1;
      list.push({
        필드번호,
        필드명: name,
        데이터타입: dataTypeName(params['@데이터타입']),
        순서: list.length + 1,
      });
    }
    return successResult('필드를 저장했습니다. (데모 데이터입니다.)');
  },

  // 상품별로 켤 필드 목록. 서버와 같이 통째로 갈아끼운다.
  TRP_T_투어_상세정보_01: ({ params }) => {
    const productNo = String(params['@상품번호'] ?? '');
    let rows = [];
    try {
      rows = JSON.parse(String(params['@예약시받을정보'] || '[]'));
    } catch (_) {
      rows = [];
    }
    const nos = (Array.isArray(rows) ? rows : [])
      .map((r) => Number(r?.필드번호))
      .filter((n) => Number.isFinite(n));
    store.productBookingFields[productNo] = nos;

    // 상세정보도 기억해야 신청금 설정이 다시 열었을 때 남아 있다.
    try {
      const detail = JSON.parse(String(params['@상세정보'] || '{}'));
      if (detail && typeof detail === 'object') {
        store.productDetails[productNo] = { ...(store.productDetails[productNo] || {}), ...detail };
      }
    } catch (_) {}

    return successResult('상세정보를 저장했습니다. (데모 데이터입니다.)');
  },

  // 저장 시 화면이 먼저 불러오는 "기준 가격정보".
  // 빈 응답을 주면 화면이 알아서 기본 구조를 만들어 쓰므로 그대로 둔다.
  RCT_S_가격예약등록정보_01: () => dataset([]),

  // ── 상담(견적) 관리 화면 ────────────────────────────────

  /**
   * 상담 목록.
   * 조회기간은 등록일 기준이다. 화면 기본값이 "1년 전 ~ 오늘" 인데 기간을 무시해 버리면
   * [적용] 을 눌러도 결과가 그대로여서 화면이 죽은 것처럼 보인다.
   */
  ITN_S_상담리스트: ({ params }) => {
    const start = params['@시작일'];
    const end = params['@종료일'];
    const rows = getConsults()
      .map(buildConsultListRow)
      .filter((row) => withinDateRange(row.등록일, start, end))
      // 최신 등록이 위로. 신규 견적을 만든 직후 화면이 목록 첫 줄을 집어 가기도 한다.
      .sort((a, b) => String(b.등록일시).localeCompare(String(a.등록일시)));
    return dataset(rows);
  },

  // 상담 한 건의 상세. 실제 SP 는 상담정보를 JSON 문자열 한 칸에 담아 주므로 형태를 맞춘다.
  TRP_S_상담정보: ({ params }) => {
    const consult = findConsult(params['@상담번호']);
    if (!consult) return dataset([]);
    return dataset([{ 상담번호: consult.상담번호, 상담정보: JSON.stringify([buildConsultInfo(consult)]) }]);
  },

  // 상담내역(상담기록). 통화한 내용·회신한 내용을 시간순으로 쌓는 칸이다.
  TRP_S_상담번호_상담기록: ({ params }) => dataset(consultNotesOf(params['@상담번호'])),

  // ── 예약 관리 화면 ──────────────────────────────────────
  // 조회기간(@시작일/@종료일)과 조회구분(@검색기준)을 실제로 반영한다.
  // 데모에서 [조회] 를 눌렀는데 결과가 그대로면 화면이 죽은 것처럼 보이기 때문이다.

  // 예약조회 탭 — 한 줄이 여행객 한 명
  WSP_S_예약현황_여행객리스트: ({ params }) =>
    dataset(
      applySaleOverrides(
        buildTravelerListRows({
          start: params['@시작일'],
          end: params['@종료일'],
          basis: params['@검색기준'],
        })
      )
    ),

  // 주문조회 · 상품별 조회 · 실적 통계 탭 — 한 줄이 판매 한 건
  WSP_S_예약현황_02: ({ params }) =>
    dataset(
      applySaleOverrides(
        buildOrderStatusRows({
          start: params['@시작일'],
          end: params['@종료일'],
          basis: params['@검색기준'],
        })
      )
    ),

  // 상품 목록의 [주문내역] 버튼 — 그 상품으로 들어온 예약만
  WSP_S_예약현황_02_상품번호: ({ params }) =>
    dataset(
      applySaleOverrides(
        buildOrderStatusRows({
          start: params['@시작일'],
          end: params['@종료일'],
          basis: params['@검색기준'],
          productNo: params['@상품번호'],
        })
      )
    ),

  // 단체조회 탭
  WSP_S_행사단체결산_메인: ({ params }) =>
    dataset(buildGroupSettlementRows({ start: params['@시작일'], end: params['@종료일'] })),

  // 실적 통계의 [미수] 카드를 눌렀을 때 뜨는 목록
  TRP_S_미수현황: ({ params }) =>
    dataset(buildOutstandingRows({ start: params['@시작일'], end: params['@종료일'] })),

  // 여행객 명단 (목록의 인원 칸에 마우스를 올리면 뜨는 팝오버 + 예약 상세 + 룸 배정)
  CW_S_예약관리_여행객리스트: ({ params }) => dataset(getRoster(params['@판매번호'])),

  // 여권사본 경로를 확인하려고 한 번 더 부르는 목록. 데모에는 사본이 없으므로 명단만 그대로 준다.
  RCT_S_판매번호별고객리스트: ({ params }) =>
    dataset(getRoster(params['@판매번호']).map((row) => ({ ...row, 여권사본경로: '' }))),

  // ── 예약 상세 화면 ──────────────────────────────────────
  TRP_S_주문조회_주문정보: ({ params }) => {
    const key = String(params['@주문번호'] ?? '');
    const order = findDemoOrder(key);
    if (!order) return dataset([]);

    const row = buildOrderInfo(order);
    // 주문자와 대리점은 이 화면에서 고칠 수 있다. 고친 값이 있으면 그걸 얹는다.
    const orderer = store.orderers[key];
    if (orderer) {
      row.주문자 = orderer.주문자;
      row.주문자휴대폰 = orderer.주문자휴대폰;
      row.주문자이메일 = orderer.주문자이메일;
      row.이메일 = orderer.주문자이메일;
    }
    const agency = store.agencies[key];
    if (agency) {
      row.대리점 = agency.대리점;
      row.대리점명 = agency.대리점;
      row.대리점번호 = agency.대리점번호;
      row.대리점코드 = agency.대리점번호;
      row.사이트 = agency.대리점 || '자사몰';
    }
    // 입금액은 통장에서 붙인 만큼 늘어난다. 가격 카드의 미수금이 이 값으로 계산된다.
    row.결제금액 = depositDataFor(order.판매번호).summary.입금액;
    return dataset([row]);
  },

  TRP_S_주문관리_판매리스트: ({ params }) => {
    const order = findDemoOrder(params['@주문번호']);
    return dataset(order ? [applySaleOverrides([buildSaleStatusRow(order)])[0]] : []);
  },

  TRP_S_예약관리_가격정보: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const order = findDemoOrderBySaleNo(key);
    if (!order) return dataset([]);

    const row = buildPriceInfo(order);
    // 취소료와 대리점 커미션은 화면에서 고칠 수 있다. 고친 값이 있으면 그걸 쓰고,
    // 넷가(우리가 실제로 손에 쥐는 돈)는 커미션이 바뀐 만큼 다시 계산한다.
    if (store.cancelFees[key] !== undefined) row.취소료원화 = store.cancelFees[key];
    if (store.commissions[key] !== undefined) {
      row.대리점커미션원화 = store.commissions[key];
      row.넷가원화 = row.고객결제가원화 - store.commissions[key];
    }
    return dataset([row]);
  },

  // 판매정보. 예약 상세는 여기서 명단편집 잠금을, 확인서는 상품명·행사기간을 읽는다.
  TRP_S_예약관리_판매정보: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const order = findDemoOrderBySaleNo(key);
    if (!order) return dataset([]);
    const row = applySaleOverrides([buildSaleInfoRow(order)])[0];
    row.명단잠금여부 = !!store.listLocked[key];
    return dataset([row]);
  },

  // 입금내역. table 이 표, table1 이 위쪽 요약(입금액/미수금)이다.
  WSP_S_입금리스트: ({ params }) => {
    const { list, summary } = depositDataFor(params['@판매번호']);
    return dataset(list, { table1: [summary] });
  },

  // ── 예약 상세 · 취소 처리 ────────────────────────────────
  // 취소 버튼을 누르면 먼저 이걸 불러서 "얼마를 떼야 하는지"를 계산해 보여준다.
  // 데모에서는 출발까지 남은 날짜로 취소 규정을 적용한 금액이 나온다.
  TRP_S_예약상태및취소료가져오기: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const order = findDemoOrderBySaleNo(key);
    if (!order) return dataset([]);
    const row = buildCancelInfo(order);
    if (store.cancelFees[key] !== undefined) row.취소료 = store.cancelFees[key];
    return dataset([row]);
  },

  // ── 예약 상세 · 통장 입금 매칭 ───────────────────────────
  // 회사 통장에 찍힌 입금 목록. 예약별이 아니라 회사 전체라서 판매번호로 거르지 않는다.
  // (돈은 통장에 먼저 들어오고, 어느 예약 것인지는 담당자가 나중에 붙인다)
  WSP_S_수납리스트_01: ({ params }) => {
    const start = String(params['@시작일'] || '').slice(0, 10);
    const end = String(params['@종료일'] || '').slice(0, 10);
    return dataset(
      getReceipts().filter((row) => {
        const day = String(row.수납일 || '').slice(0, 10);
        if (start && day < start) return false;
        if (end && day > end) return false;
        return true;
      })
    );
  },

  // 통장 입금 한 줄의 상세. 화면은 여기서 잔액을 읽어 "최대 적용 가능 금액"을 정한다.
  TRP_S_수납리스트_거래번호: ({ params }) => {
    const tx = String(params['@거래번호'] ?? '');
    const row = getReceipts().find((r) => String(r.거래번호) === tx);
    return dataset(row ? [row] : []);
  },

  // ── 예약 상세 · 예약기록 ─────────────────────────────────
  // 태그가 '업무메모' 인 줄은 상세 화면 오른쪽 카드에, 나머지는 [예약기록] 표에 나뉘어 그려진다.
  TRP_S_예약기록_판매번호: ({ params }) => dataset(getLogs(params['@판매번호'])),

  // ── 예약 상세 · 그 밖의 조회 ─────────────────────────────
  TRP_S_판매_고객요청사항: ({ params }) => {
    const order = findDemoOrderBySaleNo(params['@판매번호']);
    return dataset(order ? buildRequestItems(order) : []);
  },

  // 대리점 목록 (주문정보의 대리점을 바꿀 때 쓰는 드롭다운)
  CW_S_판매거래처: () => dataset(DEMO_AGENCIES),

  // [예약정보 입력요청] — 고객이 직접 명단을 채워 넣을 수 있는 링크를 만든다.
  // 만들어지는 주소는 실제 고객 입력 사이트다. 데모 주문번호로는 열리지 않으니
  // "링크를 만들어 복사한다" 까지만 보여주고, 링크를 눌러 보지는 말 것.
  CW_S_예약관리_고객직접입력URL: ({ params }) => {
    const order = findDemoOrderBySaleNo(params['@판매번호']);
    if (!order) return dataset([]);
    return dataset([
      {
        판매번호: order.판매번호,
        주문번호: order.주문번호,
        유니크키: `DEMO-${order.주문번호}-${order.판매번호}`,
        휴대폰번호: order.예약자.휴대폰,
      },
    ]);
  },

  // 추가 서비스(옵션)는 일부러 비워 둔다.
  // 여기에 금액을 넣으면 총 판매가 = 상품금액 + 추가서비스 + 택스 로 다시 맞춰야 하는데,
  // 그러면 예약 목록·단체조회·미수현황의 금액까지 전부 따라 움직여야 한다.
  // 데모에서 화면마다 숫자가 어긋나는 것보다 옵션 없는 예약으로 두는 편이 낫다.
  TRP_S_예약관리_추가서비스: () => dataset([]),

  // ── 저장 / 수정 / 삭제 ──────────────────────────────────
  // 데모에서는 서버에 안 보내는 대신, 화면이 살아 있는 느낌이 나도록
  // 메모리 데이터를 실제로 바꿔 준다. 새로고침하면 원래대로 돌아간다.
  TRP_T_투어_기본정보_01: ({ params }) => {
    try {
      const info = JSON.parse(String(params['@기본정보'] || '{}'));
      const product = findProduct(info.상품번호 ?? params['@상품번호']);
      if (product) {
        if (info.상품명 != null) product.상품명 = info.상품명;
        if (info.상품코드 != null) product.상품코드 = info.상품코드;
        if (info.도시 != null) product.도시 = info.도시;
        if (info.상품타입 != null) product.상품타입 = info.상품타입;
        if (info.상품담당 != null) product.상품담당 = info.상품담당;
        if (info.예약담당 != null) product.예약담당 = info.예약담당;
        if (info.상품뱃지 != null) product.상품뱃지 = info.상품뱃지;
        if (typeof info.노출여부 === 'boolean') {
          product.노출여부 = info.노출여부;
          product.판매여부 = info.노출여부;
        }
        if (params['@키워드'] != null) product.키워드 = String(params['@키워드']);
      }
    } catch (_) {}
    return successResult('저장되었습니다. (데모 데이터라 새로고침하면 처음 상태로 돌아갑니다.)');
  },

  // 스마트 상품 등록 1단계의 [다음 단계]. 여기서 상품이 "실제로" 만들어지는 지점이라
  // 데모에서도 목록에 새 상품을 하나 끼워 넣어야 2단계가 정상으로 이어진다.
  CW_T_투어_상품추가: ({ params }) => {
    const codeName = (kind, code) =>
      (DEMO_CODES[kind] || []).find((c) => String(c.코드) === String(code))?.코드명 || '';

    const productNo = store.nextProductNo;
    store.nextProductNo += 1;

    const unit = String(params['@소요시간타입'] || '012001'); // 012001=일, 012002=시간
    const amount = Number(params['@소요시간']) || 1;
    const city = codeName('도시', params['@도시번호']) || '미지정';

    getProducts().unshift({
      상품번호: productNo,
      상품코드: `DEMO-NEW-${productNo}`,
      상품명: String(params['@상품명'] || '이름 없는 상품'),
      상품종류: '투어',
      상품종류코드: '001002',
      상품타입: codeName('투어상품타입', params['@상품타입']) || '패키지',
      국가: '',
      도시: city,
      상품담당: DEMO_STAFF.한글이름,
      상품담당번호: DEMO_STAFF.유저번호,
      예약담당: DEMO_STAFF.한글이름,
      예약담당번호: DEMO_STAFF.유저번호,
      판매여부: true,
      노출여부: true,
      여행기간: unit === '012002' ? `${amount}시간` : `${amount}일`,
      소요: unit === '012002' ? '시간' : '일',
      소요시간: amount,
      최종행사일: dayFromNow(90),
      생성일시: dateTimeFromNow(0, 9, 0),
      등록일시: dateTimeFromNow(0, 9, 0),
      통화: '원화',
      통화코드: String(params['@통화코드'] || 'KRW'),
      상품뱃지: '',
      상품비고: '',
      키워드: '',
    });

    return dataset([{ 상품번호: productNo, 결과: 'success', 메시지: '상품이 생성되었습니다.' }]);
  },

  // 가격/재고 [저장] — 고른 날짜 전부에 같은 값을 넣는다.
  RCT_T_상품가격_투어_03: ({ params }) => {
    try {
      const info = JSON.parse(String(params['@가격정보'] || '{}'));
      const planNo = Number(info?.선택요금제?.[0]?.가격옵션번호);
      if (!planNo || !DEMO_PLAN_BASE[planNo]) return successResult();

      const sell = readAmountRow(info.판매가, '판매가') || DEMO_PLAN_BASE[planNo].sell;
      const tax = readAmountRow(info.판매가, 'Tax및유류할증료') || DEMO_PLAN_BASE[planNo].tax;

      const periods = Array.isArray(info.적용기간) ? info.적용기간 : [];
      const dates = periods.flatMap(expandPeriod);
      dates.forEach((date) => {
        store.priceOverrides[`${planNo}|${date}`] = { sell, tax };
      });

      return successResult(`${dates.length}개 날짜에 저장했습니다. (데모 데이터입니다.)`);
    } catch (_) {
      return successResult();
    }
  },

  // 재고 저장 — @선택행사 는 [{날짜, 시간, 자원번호}] 형태다.
  TRP_T_투어세션관리_선택DATA재고수정_01: ({ params }) => {
    try {
      const seats = Number(String(params['@재고수'] ?? '').replace(/[^0-9]/g, ''));
      const events = JSON.parse(String(params['@선택행사'] || '[]'));
      if (Array.isArray(events) && Number.isFinite(seats)) {
        events.forEach((e) => {
          const date = String(e?.날짜 || '').slice(0, 10);
          const resourceNo = Number(e?.자원번호);
          if (date && resourceNo) store.stockOverrides[`${resourceNo}|${date}`] = seats;
        });
      }
    } catch (_) {}
    return successResult('재고를 저장했습니다. (데모 데이터입니다.)');
  },

  // 마감 설정 — 자리가 남아 있어도 그 날짜를 더 못 팔게 막는다.
  TRP_T_투어세션관리_선택DATA마감설정_03: ({ params }) => {
    try {
      const closed = String(params['@마감여부']).toLowerCase() === 'true' || params['@마감여부'] === true;
      const events = JSON.parse(String(params['@선택행사'] || '[]'));
      if (Array.isArray(events)) {
        events.forEach((e) => {
          const date = String(e?.날짜 || '').slice(0, 10);
          const resourceNo = Number(e?.자원번호);
          if (date && resourceNo) store.closedOverrides[`${resourceNo}|${date}`] = closed;
        });
      }
    } catch (_) {}
    return successResult('마감 설정을 저장했습니다. (데모 데이터입니다.)');
  },

  // 예약 목록에서 판매비고를 고쳤을 때
  TRP_T_판매비고저장: ({ params }) => {
    const saleNo = String(params['@판매번호'] ?? '');
    if (saleNo) store.saleMemos[saleNo] = String(params['@판매비고'] ?? '');
    return successResult('판매비고를 저장했습니다. (데모 데이터입니다.)');
  },

  // 여러 예약을 골라 상태를 한 번에 바꿨을 때
  TRP_T_예약일괄수정: ({ params }) => {
    try {
      const list = JSON.parse(String(params['@예약리스트'] || '[]'));
      const code = String(params['@값'] ?? '');
      if (Array.isArray(list) && code) {
        list.forEach((row) => {
          const saleNo = String(row?.판매번호 ?? '');
          if (saleNo) store.saleStatuses[saleNo] = code;
        });
      }
      return successResult(`${Array.isArray(list) ? list.length : 0}건의 예약상태를 바꿨습니다. (데모 데이터입니다.)`);
    } catch (_) {
      return successResult();
    }
  },

  TRP_T_투어_상품삭제: ({ params }) => {
    const key = String(params['@상품번호'] ?? '');
    const list = getProducts();
    const idx = list.findIndex((p) => String(p.상품번호) === key);
    if (idx >= 0) list.splice(idx, 1);
    return successResult('삭제되었습니다. (데모 데이터입니다.)');
  },

  // ── 예약 상세 · 취소 처리 (저장) ─────────────────────────
  // 취소는 상태 하나만 바꾸는 게 아니다. 예약상태가 '취소완료'로 가고, 취소료가 가격정보에
  // 붙고, 기록이 남는다. 셋 중 하나라도 빠지면 데모에서 "취소했는데 아무것도 안 바뀐" 화면이 된다.
  TRP_T_판매_판매취소: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const fee = toNumber(params['@부과취소료']);
    if (!key) return successResult();

    store.saleStatuses[key] = '049029'; // 취소완료
    store.cancelFees[key] = fee;

    const { summary } = depositDataFor(key);
    addLog(key, '취소', `예약을 취소 처리했습니다. 취소료 ${won(fee)} / 환불 예정 ${won(Math.max(0, summary.입금액 - fee))}.`);
    return successResult('예약을 취소했습니다. (데모 데이터입니다.)');
  },

  // 취소한 예약을 되돌린다.
  TRP_T_취소상품예약복원: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    if (!key) return successResult();

    const order = findDemoOrderBySaleNo(key);
    // 원래부터 취소 상태로 깔려 있던 예약은 지우기만 하면 다시 취소로 돌아간다. 확정으로 되돌린다.
    if (order && CANCEL_STATUS_CODES.includes(String(order.예약상태코드))) {
      store.saleStatuses[key] = '049011';
    } else {
      delete store.saleStatuses[key];
    }
    store.cancelFees[key] = 0;

    addLog(key, '복원', '취소했던 예약을 복원했습니다. 취소료는 0원으로 되돌렸습니다.');
    return successResult('예약을 복원했습니다. (데모 데이터입니다.)');
  },

  CW_T_예약관리_취소료수정: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const fee = toNumber(params['@취소료']);
    if (!key) return successResult();
    store.cancelFees[key] = fee;
    addLog(key, '취소', `취소료를 ${won(fee)}으로 고쳤습니다.`);
    return successResult('취소료를 저장했습니다. (데모 데이터입니다.)');
  },

  CW_T_예약관리_대리점커미션수정: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const amount = toNumber(params['@금액']);
    if (!key) return successResult();
    store.commissions[key] = amount;
    addLog(key, '가격', `대리점 커미션을 ${won(amount)}으로 고쳤습니다.`);
    return successResult('대리점 커미션을 저장했습니다. (데모 데이터입니다.)');
  },

  // 예약상태 드롭다운. 화면은 단계별로 @예약상태1~3 을 채워 보내는데, 마지막으로 채워진 게 최종 상태다.
  TRP_T_예약관리_판매상태: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const code = String(params['@예약상태3'] || params['@예약상태2'] || params['@예약상태1'] || '');
    if (!key || !code) return successResult();

    store.saleStatuses[key] = code;
    const name = (DEMO_SALE_STATUS.find((s) => s.코드 === code) || {}).코드명 || code;
    addLog(key, '상태변경', `예약상태를 ${name}(으)로 바꿨습니다.`);
    return successResult(`예약상태를 ${name}(으)로 바꿨습니다. (데모 데이터입니다.)`);
  },

  // ── 예약 상세 · 통장 입금 매칭 (저장) ────────────────────
  // 통장에 찍힌 돈을 이 예약의 입금으로 붙인다.
  // 붙인 만큼 통장 줄의 잔액이 줄고(그래서 목록에서 사라지고), 예약의 미수금이 줄어든다.
  TRP_T_수납입금적용: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const tx = String(params['@거래번호'] ?? '');
    const receipt = getReceipts().find((r) => String(r.거래번호) === tx);
    if (!key || !receipt) return successResult();

    const 요청액 = toNumber(params['@입금액2']);
    const 적용액 = Math.max(0, Math.min(요청액, toNumber(receipt.잔액)));
    if (적용액 <= 0) return successResult('적용할 금액이 없습니다. (데모 데이터입니다.)');

    receipt.총입금액 = toNumber(receipt.총입금액) + 적용액;
    receipt.잔액 = Math.max(0, toNumber(receipt.잔액) - 적용액);

    addDepositRow(key, {
      수납일: receipt.수납일,
      송금카드소유자: receipt.송급카드소유자,
      결제구분: receipt.종류,
      입금액: 적용액,
      비고: String(params['@비고'] || '통장 입금 적용'),
      거래번호: tx,
    });

    addLog(key, '입금', `통장 입금 ${won(적용액)}을 이 예약에 적용했습니다. (${receipt.송급카드소유자})`);
    return successResult(`${won(적용액)}을 입금 처리했습니다. (데모 데이터입니다.)`);
  },

  // 통장에 안 찍힌 입금을 손으로 등록한다. (현금으로 받았거나 통장 연동이 없는 경우)
  CW_T_수납관리: ({ params }) => {
    const amount = toNumber(params['@수납액']);
    if (String(params['@구분'] || 'I') !== 'I' || amount <= 0) return successResult();

    store.nextReceiptNo += 1;
    getReceipts().unshift({
      거래번호: `TX${store.nextReceiptNo}`,
      거래일련번호: store.nextReceiptNo,
      수납일: String(params['@거래일시'] || '').slice(0, 10),
      송급카드소유자: String(params['@비고1'] || ''),
      종류: '계좌이체',
      은행명: (DEMO_BANK_ACCOUNTS.find((a) => a.계좌번호 === String(params['@계좌번호'])) || {}).은행명 || '신한은행',
      수납액: amount,
      총입금액: 0,
      잔액: amount,
      수납통화코드: '058001',
      비고: String(params['@비고2'] || ''),
    });
    return successResult('수납을 등록했습니다. (데모 데이터입니다.)');
  },

  // 통장 입금 줄 자체를 지운다. (잘못 등록한 수납)
  CW_T_수납내역삭제: ({ params }) => {
    const seq = String(params['@거래일련번호'] ?? '');
    const rows = getReceipts();
    const idx = rows.findIndex((r) => String(r.거래일련번호) === seq);
    if (idx >= 0) rows.splice(idx, 1);
    return successResult('수납 내역을 삭제했습니다. (데모 데이터입니다.)');
  },

  // 예약에 붙여 둔 입금을 뗀다.
  // 우리가 통장에서 붙인 것이면 그 돈을 통장으로 되돌려 놓아야 총액이 맞는다.
  WSP_T_주문결제_삭제_01: ({ params }) => {
    const paymentNo = String(params['@결제번호'] ?? '');
    if (!paymentNo) return successResult();

    const saleKey = Object.keys(store.deposits).find((key) =>
      (store.deposits[key] || []).some((row) => String(row.결제번호) === paymentNo)
    );

    if (saleKey) {
      const rows = store.deposits[saleKey];
      const [removed] = rows.splice(rows.findIndex((row) => String(row.결제번호) === paymentNo), 1);
      const receipt = getReceipts().find((r) => String(r.거래번호) === String(removed.거래번호));
      if (receipt) {
        receipt.총입금액 = Math.max(0, toNumber(receipt.총입금액) - toNumber(removed.입금액));
        receipt.잔액 = toNumber(receipt.잔액) + toNumber(removed.입금액);
      }
      addLog(saleKey, '입금', `입금 ${won(removed.입금액)}을 취소하고 통장으로 되돌렸습니다.`);
      return successResult('입금 내역을 삭제했습니다. (데모 데이터입니다.)');
    }

    // 원래 깔려 있던 입금이다. 결제번호는 판매번호에서 만들어 두었으므로 거꾸로 판매번호를 얻는다.
    const saleNo = String(Math.floor(toNumber(paymentNo) / 10));
    markDepositRemoved(saleNo, paymentNo);
    addLog(saleNo, '입금', '입금 내역 한 줄을 삭제했습니다.');
    return successResult('입금 내역을 삭제했습니다. (데모 데이터입니다.)');
  },

  // ── 예약 상세 · 여행객 명단 / 룸 배정 (저장) ─────────────
  // 명단 저장과 룸번호 저장이 같은 프로시저를 쓴다. 룸번호는 필드번호 -1 로 들어온다.
  TRP_T_예약관리_판매상태및여행객수정_택스포함_14: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    if (!key) return successResult();

    const roster = getRoster(key);
    const byNo = new Map(roster.map((row) => [String(row.여행객번호), row]));

    parseJsonParam(params['@여행객_JSON'], []).forEach((entry) => {
      const row = byNo.get(String(entry?.여행객번호));
      if (row) applyTravelerField(row, entry?.필드번호, entry?.값 ?? entry?.필드값);
    });

    parseJsonParam(params['@여행객상품금액_JSON'], []).forEach((entry) => {
      const row = byNo.get(String(entry?.여행객번호));
      if (!row) return;
      row.상품금액원화 = toNumber(entry?.상품금액);
      row.공급단가 = toNumber(entry?.공급단가);
      row.택스및유류원화 = toNumber(entry?.택스및유류);
    });

    parseJsonParam(params['@여행객순번_JSON'], []).forEach((entry) => {
      const row = byNo.get(String(entry?.여행객번호));
      if (!row) return;
      if (entry?.여행객종류) row.여행객종류 = entry.여행객종류;
      row.선발권동의 = !!entry?.선발권동의;
      row.명단확인 = !!entry?.명단확인;
      row.순번 = Number(entry?.순번) || row.순번;
    });

    store.travelers[key] = roster.slice().sort((a, b) => (Number(a.순번) || 0) - (Number(b.순번) || 0));

    const 방배정 = store.travelers[key].filter((row) => String(row.룸번호 ?? '').trim() !== '').length;
    addLog(key, '명단', 방배정 > 0 ? `여행객 명단을 저장했습니다. (룸 배정 ${방배정}명)` : '여행객 명단을 저장했습니다.');
    return successResult('저장했습니다. (데모 데이터입니다.)');
  },

  // 명단에 사람을 넣거나 뺄 때. 남길 사람 전체가 @여행객_JSON 으로 오므로,
  // 목록에 없는 사람은 빠진 것으로 보고 명단을 통째로 다시 만든다.
  CW_T_예약관리_여행객변경: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    if (!key) return successResult();

    const before = getRoster(key);
    const byNo = new Map(before.map((row) => [String(row.여행객번호), row]));

    // 같은 사람의 필드가 여러 줄로 나뉘어 오므로 사람 단위로 먼저 묶는다.
    const order = [];
    const grouped = new Map();
    parseJsonParam(params['@여행객_JSON'], []).forEach((entry) => {
      const id = String(entry?.여행객번호 ?? '');
      if (!id) return;
      if (!grouped.has(id)) {
        grouped.set(id, { 연령: entry?.연령, fields: [] });
        order.push(id);
      }
      const bucket = grouped.get(id);
      bucket.fields.push(entry);
      if (entry?.연령) bucket.연령 = entry.연령;
    });

    const after = order.map((id, idx) => {
      // 화면이 붙여 보낸 임시 번호(UUID)면 여기서 진짜 번호를 매겨 준다.
      const row = byNo.get(id) || newTravelerRow(key);
      grouped.get(id).fields.forEach((entry) => applyTravelerField(row, entry?.필드번호, entry?.필드값));
      row.연령구분 = grouped.get(id).연령 || row.연령구분 || '성인';
      row.순번 = idx + 1;
      return row;
    });

    store.travelers[key] = after;

    const 차이 = after.length - before.length;
    if (차이 > 0) addLog(key, '명단', `여행객 ${차이}명을 명단에 추가했습니다. (총 ${after.length}명)`);
    else if (차이 < 0) addLog(key, '명단', `여행객 ${-차이}명을 명단에서 뺐습니다. (총 ${after.length}명)`);
    else addLog(key, '명단', '여행객 명단을 저장했습니다.');

    return successResult('명단을 저장했습니다. (데모 데이터입니다.)');
  },

  // 명단편집 잠금. 출발이 임박해 명단이 더 바뀌면 안 될 때 잠근다.
  WSP_T_판매_명단락여부: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const locked = params['@상태'] === true || String(params['@상태']).toLowerCase() === 'true';
    if (key) {
      store.listLocked[key] = locked;
      addLog(key, '명단', locked ? '명단편집을 잠갔습니다.' : '명단편집 잠금을 풀었습니다.');
    }
    return successResult('설정을 저장했습니다. (데모 데이터입니다.)');
  },

  // ── 예약 상세 · 업무메모 (예약기록) ──────────────────────
  // 업무메모는 예약기록의 한 종류다. 태그만 '업무메모' 로 달라서 상세 화면 오른쪽 카드에 따로 뜬다.
  TRP_T_예약메모기록: ({ params }) => {
    const key = String(params['@판매번호'] ?? '');
    const memo = String(params['@메모'] ?? '');
    if (!key || !memo.trim()) return successResult();
    const row = addLog(key, '업무메모', memo);
    return dataset([{ 참조번호: row.참조번호, 결과: 'success', 메시지: '업무메모를 추가했습니다.' }]);
  },

  TRP_T_예약메모기록수정: ({ params }) => {
    const found = findLog(params['@참조번호']);
    if (found && found.index >= 0) {
      found.rows[found.index] = {
        ...found.rows[found.index],
        내용: String(params['@메모'] ?? ''),
        작성일: nowStamp(),
      };
    }
    return successResult('업무메모를 수정했습니다. (데모 데이터입니다.)');
  },

  TRP_T_예약메모기록삭제: ({ params }) => {
    const found = findLog(params['@참조번호']);
    if (found && found.index >= 0) found.rows.splice(found.index, 1);
    return successResult('업무메모를 삭제했습니다. (데모 데이터입니다.)');
  },

  // ── 예약 상세 · 주문자 / 대리점 수정 ─────────────────────
  TRP_T_예약관리_주문_주문자정보: ({ params }) => {
    const key = String(params['@주문번호'] ?? '');
    if (!key) return successResult();
    store.orderers[key] = {
      주문자: String(params['@주문자'] ?? ''),
      주문자휴대폰: String(params['@주문자휴대폰'] ?? ''),
      주문자이메일: String(params['@주문자이메일'] ?? ''),
    };
    const order = findDemoOrder(key);
    if (order) addLog(order.판매번호, '주문', `주문자 정보를 ${store.orderers[key].주문자} 로 고쳤습니다.`);
    return successResult('주문자 정보를 저장했습니다. (데모 데이터입니다.)');
  },

  RCT_T_예약관리대리점수정: ({ params }) => {
    const key = String(params['@주문번호'] ?? '');
    if (!key) return successResult();
    const name = String(params['@대리점명'] ?? '');
    store.agencies[key] = { 대리점: name, 대리점번호: String(params['@대리점번호'] ?? '') };
    const order = findDemoOrder(key);
    if (order) addLog(order.판매번호, '주문', `대리점을 ${name || '직판'} 으로 바꿨습니다.`);
    return successResult('대리점을 저장했습니다. (데모 데이터입니다.)');
  },

  // ── 상담(견적) 관리 (저장) ───────────────────────────────

  /**
   * 상담정보 저장.
   * 상담상세의 [저장] 과 상태 드롭다운, 그리고 "상품번호만 다시 연결" 이 모두 이 하나를 쓴다.
   * 셋을 구분하지 않고 보내온 상담정보를 그대로 덮어씌우면 된다.
   */
  TRP_T_상담정보_01: ({ params }) => {
    const info = parseConsultInfo(params['@상담정보']);
    if (!info) return successResult();
    const consult = findConsult(info.상담번호);
    if (!consult) return successResult();
    applyConsultInfo(consult, info);
    return successResult('상담정보를 저장했습니다. (데모 데이터입니다.)');
  },

  // 신규 견적 등록. 화면은 응답의 상담번호를 받아 곧바로 상담상세로 넘어간다.
  ITN_T_상담저장: ({ params }) => {
    const consult = newConsult(parseConsultInfo(params['@상담정보']));
    return dataset([
      { 상담번호: consult.상담번호, 결과: 'success', 성공여부: true, 메시지: '견적을 만들었습니다.' },
    ]);
  },

  // 상담내역 추가 / 수정. 상담기록번호가 0 이면 새 줄이다.
  TRP_T_상담기록정보: ({ params }) => {
    const consultNo = String(params['@상담번호'] ?? '');
    const recordNo = toNumber(params['@상담기록번호']);
    const content = String(params['@내용'] ?? '');
    const rows = consultNotesOf(consultNo);

    if (recordNo > 0) {
      const hit = rows.find((row) => Number(row.상담기록번호) === recordNo);
      if (hit) {
        hit.내용 = content;
        hit.등록일시 = nowStamp();
      }
      return successResult('상담내역을 고쳤습니다. (데모 데이터입니다.)');
    }

    store.nextConsultNoteNo += 1;
    rows.unshift({
      상담기록번호: store.nextConsultNoteNo,
      상담번호: Number(consultNo) || consultNo,
      내용: content,
      등록자: DEMO_STAFF.한글이름,
      등록일시: nowStamp(),
      등록일: nowStamp().slice(0, 10),
      유저번호: DEMO_USER_NO,
    });
    return successResult('상담내역을 남겼습니다. (데모 데이터입니다.)');
  },

  // 상담내역 삭제. 화면은 어느 상담의 기록인지 알려주지 않으므로 번호로 찾아 지운다.
  TRP_T_상담기록정보삭제: ({ params }) => {
    const recordNo = toNumber(params['@상담기록번호']);
    Object.keys(store.consultNotes).forEach((key) => {
      store.consultNotes[key] = store.consultNotes[key].filter(
        (row) => Number(row.상담기록번호) !== recordNo
      );
    });
    return successResult('상담내역을 지웠습니다. (데모 데이터입니다.)');
  },

  // 상담 삭제. 상담은 예약과 달리 실제로 목록에서 사라진다.
  TRP_T_상담정보삭제: ({ params }) => {
    const key = String(params['@상담번호'] ?? '');
    store.consults = getConsults().filter((c) => String(c.상담번호) !== key);
    delete store.consultNotes[key];
    return successResult('상담을 지웠습니다. (데모 데이터입니다.)');
  },
};

// ────────────────────────────────────────────────────────────────
// 인터셉터
// ────────────────────────────────────────────────────────────────

/** 등록되지 않은 프로시저의 기본 응답 */
function fallbackResponse(procedure) {
  // _S_ 는 조회, _T_/_U_/_D_ 는 저장·수정·삭제라는 명명 규칙을 따른다.
  const isQuery = /_S_/.test(procedure);
  if (isQuery) {
    if (process.env.NODE_ENV !== 'production') {
      console.info(`[demo] 미등록 조회 프로시저 → 빈 목록으로 응답: ${procedure}`);
    }
    return dataset([], { table2: [], table3: [], empt1: [], empt2: [] });
  }
  if (process.env.NODE_ENV !== 'production') {
    console.info(`[demo] 미등록 저장 프로시저 → 성공으로 응답: ${procedure}`);
  }
  return successResult();
}

async function handleDemoRequest(input, init) {
  const urlString = toUrlString(input);
  const url = new URL(urlString, window.location.origin);

  // 파일 업로드는 업로드된 척만 한다.
  if (/\/Upload\//i.test(url.pathname)) {
    await delay(FAKE_LATENCY_MS);
    return jsonResponse({ url: '', fileUrl: '', message: 'demo' });
  }

  const rawProcedure = url.searchParams.get('procedureName');
  if (!rawProcedure) {
    await delay(FAKE_LATENCY_MS);
    return jsonResponse(dataset([]));
  }

  const procedure = decodeProcedureName(rawProcedure);

  // Request 객체로 넘어온 경우에도 본문을 읽을 수 있게 처리
  let body = init?.body;
  if (body == null && input && typeof input.clone === 'function') {
    try {
      body = await input.clone().text();
    } catch (_) {}
  }

  const ctx = {
    procedure,
    params: parseParams(body),
    body,
    url: urlString,
    method: String(init?.method || input?.method || 'GET').toUpperCase(),
  };

  const handler = HANDLERS[procedure];
  const payload = handler ? handler(ctx) : fallbackResponse(procedure);

  await delay(FAKE_LATENCY_MS);
  return jsonResponse(payload);
}

let installed = false;

/**
 * fetch 인터셉터를 설치한다. 앱 시작 시 한 번만 부르면 된다.
 * 데모 모드가 아닐 때는 원래 fetch 를 그대로 통과시키므로 실제 사용에는 영향이 없다.
 */
export function installDemoApi() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    if (!isDemoMode()) return originalFetch(input, init);

    const urlString = toUrlString(input);
    if (!isApiUrl(urlString)) return originalFetch(input, init);

    try {
      return await handleDemoRequest(input, init);
    } catch (error) {
      console.error('[demo] 샘플 응답 생성 실패:', error);
      return jsonResponse(dataset([]));
    }
  };
}
